import React, { useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import firebase from '../../Firebase';
import { sliderNumber, sliderLabel, valueAtPosition, positionOfValue, rangeInputProps, sliderRange } from '../../utils/sliderField';
import './ChatSliderWidget.css';

// A slider question from the member's community profile (e.g. "Suggest
// people within"), put in the chat by the community bot so they can set it
// themselves — the bot can't change it for them. Sent as a bot message whose
// text is SLIDER_WIDGET_PREFIX + {"field": …}; the prefix must match
// backend/controllers/Functions/communityChatBot.js. Reads and saves only
// that one answer (communiUser/sliderAnswer, setSliderAnswer).
export const SLIDER_WIDGET_PREFIX = '__SLIDER_WIDGET__';

const call = async (path, body) => {
  const token = await firebase.auth().currentUser?.getIdToken();
  const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/communiUser/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-CSRF-Token': Cookies.get('mytoken') },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const ChatSliderWidget = ({ communityId, field }) => {
  const [config, setConfig] = useState(null);
  const [value, setValue] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | saving | saved | saveError | error
  const lastSaved = useRef(null);

  useEffect(() => {
    let live = true;
    call('sliderAnswer', { communityId, field })
      .then((d) => {
        if (!live) return;
        setConfig(d.config);
        const saved = sliderNumber(d.value, d.config);
        const start = Number.isFinite(Number(d.config?.start)) ? Number(d.config.start) : sliderRange(d.config).min;
        setValue(saved ?? start);
        lastSaved.current = saved;
        setState('ready');
      })
      .catch(() => live && setState('error'));
    return () => { live = false; };
  }, [communityId, field]);

  if (state === 'loading') return <div className="chatSlider chatSlider--muted">Loading…</div>;
  if (state === 'error' || !config) return <div className="chatSlider chatSlider--muted">Couldn&apos;t load this — you can change it on your Profile page.</div>;

  // Saved when they let go of the handle, like the profile page's slider.
  const save = async () => {
    if (value === lastSaved.current) return;
    setState('saving');
    try {
      await call('setSliderAnswer', { communityId, field, value });
      lastSaved.current = value;
      setState('saved');
    } catch {
      setState('saveError');
    }
  };

  return (
    <div className="chatSlider">
      <div className="chatSlider-head">
        <span>{field}</span>
        <strong>{sliderLabel(value, config)}</strong>
      </div>
      <input
        type="range"
        {...rangeInputProps(config)}
        value={positionOfValue(value, config) ?? 0}
        onChange={(e) => { setValue(valueAtPosition(e.target.value, config)); setState('ready'); }}
        onMouseUp={save}
        onTouchEnd={save}
        onKeyUp={save}
        aria-label={field}
        aria-valuetext={sliderLabel(value, config)}
      />
      <div className="chatSlider-scale" aria-hidden="true">
        <span>{sliderLabel(sliderRange(config).min, config)}</span>
        <span>{sliderLabel(sliderRange(config).max, config)}</span>
      </div>
      <div className="chatSlider-status" aria-live="polite">
        {state === 'saving' ? 'Saving…'
          : state === 'saved' ? 'Saved ✓'
            : state === 'saveError' ? "Couldn't save — try again, or change it on your Profile page"
              : 'Drag to set — it saves when you let go'}
      </div>
    </div>
  );
};

export default ChatSliderWidget;
